import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { HardHat, ArrowRight, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLE_LABEL = {
  admin: "Administrator",
  project_manager: "Project Manager",
  contractor: "Contractor",
};

export default function InviteAcceptPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/invites/${token}`)
      .then((r) => {
        setInfo(r.data);
        if (r.data.email) setEmail(r.data.email);
      })
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Invite invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { name, password };
      if (!info.email) payload.email = email;
      await api.post(`/invites/${token}/accept`, payload);
      await refresh();
      toast.success("Welcome aboard!");
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-md ctrl-card p-8">
        <div className="flex items-center gap-2.5 mb-6">
          <div className="w-9 h-9 bg-[hsl(var(--accent))] rounded-sm flex items-center justify-center">
            <HardHat size={18} weight="bold" className="text-white" />
          </div>
          <div>
            <div className="font-display font-black text-base leading-none">PROCURE</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-0.5">
              {info?.role ? `${ROLE_LABEL[info.role]} Invite` : "Account Invite"}
            </div>
          </div>
        </div>

        {err ? (
          <div className="text-center py-6">
            <div className="w-12 h-12 rounded-full bg-red-50 text-red-600 mx-auto flex items-center justify-center mb-3">
              <Warning size={22} weight="bold" />
            </div>
            <p className="font-display text-lg font-bold tracking-tight">{err}</p>
            <Link
              to="/login"
              className="inline-flex items-center gap-1 mt-4 text-sm text-[hsl(var(--primary))] font-semibold hover:underline"
              data-testid="invite-back-to-login"
            >
              Go to sign in <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        ) : !info ? (
          <div className="h-32 bg-zinc-100 rounded animate-pulse" />
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="invite-form">
            <div className="space-y-1">
              <span className="label-eyebrow">You&apos;ve been invited as</span>
              <h2 className="font-display text-2xl font-bold tracking-tight">
                {ROLE_LABEL[info.role] || "Team Member"}
              </h2>
              {info.project_name && (
                <p className="text-sm text-muted-foreground">
                  Project: <span className="font-semibold text-foreground">{info.project_name}</span>
                </p>
              )}
              {info.contractor_name && (
                <p className="text-sm text-muted-foreground">
                  Contractor account: <span className="font-semibold text-foreground">{info.contractor_name}</span>
                </p>
              )}
              {info.company && !info.contractor_name && (
                <p className="text-sm text-muted-foreground">
                  Company: <span className="font-semibold text-foreground">{info.company}</span>
                </p>
              )}
              {info.email && (
                <p className="text-xs text-muted-foreground font-mono mt-1" data-testid="invite-email-preset">
                  Account will be created with: {info.email}
                </p>
              )}
            </div>

            <div className="space-y-3 pt-2 border-t border-border">
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Your name</Label>
                <Input
                  required value={name} onChange={(e) => setName(e.target.value)}
                  data-testid="invite-name-input"
                />
              </div>
              {!info.email && (
                <div className="space-y-1.5">
                  <Label className="label-eyebrow">Email</Label>
                  <Input
                    type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="invite-email-input"
                  />
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Choose a password</Label>
                <Input
                  type="password" minLength={6} required
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="invite-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={busy}
              className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 rounded-md py-6 text-sm font-semibold tracking-wide uppercase"
              data-testid="invite-accept-button"
            >
              {busy ? "Setting up..." : <><CheckCircle size={16} weight="bold" className="mr-2" /> Accept & sign in</>}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
